document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('header.header');

  // Search Drawer Handling
  const searchDrawer = document.querySelector('[data-search-drawer]');
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchTrigger = document.querySelector('[data-search-trigger]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInputs = Array.from(document.querySelectorAll('[data-search-input]'));
  const searchResults = document.querySelector('[data-search-results]');
  const searchResultsGrid = document.querySelector('[data-search-results-grid]');
  const searchEmpty = document.querySelector('[data-search-empty]');
  const searchViewAll = document.querySelector('[data-search-view-all]');
  const moneyFormat = searchDrawer?.getAttribute('data-money-format') || '';
  const searchNoResultsTemplate =
    searchDrawer?.getAttribute('data-search-no-results') ||
    window.SculptStrings?.searchNoResults ||
    'No results found for "{{ terms }}"';
  const searchPageUrl = searchDrawer?.getAttribute('data-search-url') || '/search';
  const predictiveSearchUrl = searchDrawer?.getAttribute('data-predictive-search-url') || '/search/suggest';
  let searchRequestId = 0;
  let searchDebounceTimer;

  const isMobileSearch = () => window.matchMedia('(max-width: 1024px)').matches;

  const getActiveSearchInput = () => {
    return (
      document.querySelector('.header__search-input--panel') ||
      document.querySelector('.header__search-input--mobile') ||
      searchInputs[0]
    );
  };

  const formatSearchMoney = (price) => {
    const cents = Math.round(Number.parseFloat(price || 0) * 100);
    const noDecimalFormat = moneyFormat
      .replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator')
      .replace('amount_with_space_separator', 'amount_no_decimals_with_space_separator')
      .replace('{{amount}}', '{{amount_no_decimals}}')
      .replace('{{ amount }}', '{{amount_no_decimals}}');

    if (window.Shopify?.formatMoney && noDecimalFormat) {
      return window.Shopify.formatMoney(cents, noDecimalFormat);
    }

    const whole = Math.round(cents / 100).toString();
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (moneyFormat) {
      return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, grouped);
    }
    return grouped;
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightQuery = (text, query) => {
    const safeText = escapeHtml(text);
    const trimmed = String(query || '').trim();
    if (!trimmed) return safeText;
    const pattern = escapeRegExp(escapeHtml(trimmed));
    if (!pattern) return safeText;
    return safeText.replace(new RegExp(`(${pattern})`, 'gi'), '<strong>$1</strong>');
  };

  const getProductImage = (product) => {
    const imageUrl = product?.featured_image?.url || product?.image || '';
    if (!imageUrl) return '';
    if (imageUrl.includes('?')) return `${imageUrl}&width=500`;
    return `${imageUrl}?width=500`;
  };

  const markSearchReady = () => {
    searchDrawer?.classList.add('is-ready');
    searchOverlay?.classList.add('is-ready');
  };

  const isSearchPanelOpen = () =>
    Boolean(header?.classList.contains('header--search-panel-open') || header?.classList.contains('header--search-open'));

  const syncSearchFocusability = (isOpen) => {
    searchInputs.forEach((input) => {
      if (isOpen) {
        input.removeAttribute('tabindex');
        input.removeAttribute('readonly');
      } else {
        input.setAttribute('tabindex', '-1');
        input.setAttribute('readonly', 'readonly');
        input.blur();
      }
    });

    if (searchDrawer) {
      searchDrawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      if (isOpen) {
        searchDrawer.removeAttribute('inert');
      } else {
        searchDrawer.setAttribute('inert', '');
      }
    }
  };

  const setSearchPanelOpen = (isOpen) => {
    if (isOpen) markSearchReady();
    if (header) {
      const bottom = Math.max(0, Math.ceil(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
    }

    header?.classList.toggle('header--search-panel-open', isOpen);
    searchDrawer?.classList.toggle('header__search-drawer--open', isOpen);
    searchOverlay?.classList.toggle('header__search-overlay--open', isOpen);
    searchTrigger?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    syncSearchFocusability(isOpen);

    if (!isOpen) {
      setSearchResultsOpen(false);
      searchInputs.forEach((input) => {
        input.value = '';
        input.blur();
      });
    }
  };

  const setSearchResultsOpen = (isOpen) => {
    if (isOpen) markSearchReady();
    if (header) {
      const bottom = Math.max(0, Math.ceil(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
    }

    header?.classList.toggle('header--search-open', isOpen);

    if (isOpen) {
      searchDrawer?.classList.add('header__search-drawer--open');
      searchOverlay?.classList.add('header__search-overlay--open');
      syncSearchFocusability(true);
    } else if (!header?.classList.contains('header--search-panel-open')) {
      searchDrawer?.classList.remove('header__search-drawer--open');
      searchOverlay?.classList.remove('header__search-overlay--open');
      syncSearchFocusability(false);
    }

    if (!isOpen) {
      if (searchResults) searchResults.hidden = true;
      if (searchResultsGrid) searchResultsGrid.innerHTML = '';
      if (searchEmpty) searchEmpty.hidden = true;
      if (searchViewAll) searchViewAll.hidden = true;
    }
  };

  const renderSearchResults = (products, query) => {
    if (!searchResults) return;

    const limitedProducts = Array.isArray(products) ? products.slice(0, 5) : [];
    const hasProducts = limitedProducts.length > 0;
    searchResults.hidden = false;
    header?.classList.add('header--search-panel-open');
    setSearchResultsOpen(true);

    if (searchResultsGrid) {
      searchResultsGrid.innerHTML = hasProducts
        ? limitedProducts
            .map((product) => {
              const image = getProductImage(product);
              const title = escapeHtml(product.title);
              const highlightedTitle = highlightQuery(product.title, query);
              const url = escapeHtml(product.url);
              const price = formatSearchMoney(product.price);
              return `
                <a class="header__search-product" href="${url}">
                  <div class="header__search-product-media">
                    ${image ? `<img src="${escapeHtml(image)}" alt="${title}" width="400" height="533" loading="lazy">` : ''}
                  </div>
                  <p class="header__search-product-title">${highlightedTitle}</p>
                  <p class="header__search-product-price">${escapeHtml(price)}</p>
                </a>
              `;
            })
            .join('')
        : '';
    }

    if (searchEmpty) {
      searchEmpty.hidden = hasProducts;
      searchEmpty.textContent = hasProducts
        ? ''
        : searchNoResultsTemplate.replace(/\{\{\s*terms\s*\}\}/g, query);
    }

    if (searchViewAll) {
      searchViewAll.hidden = !query;
      searchViewAll.href = `${searchPageUrl}?q=${encodeURIComponent(query)}&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last`;
    }
  };

  const fetchPredictiveSearch = async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setSearchResultsOpen(false);
      return;
    }

    const requestId = ++searchRequestId;
    const suggestUrl = new URL(predictiveSearchUrl, window.location.origin);
    if (!suggestUrl.pathname.endsWith('.json')) {
      suggestUrl.pathname = `${suggestUrl.pathname}.json`;
    }
    suggestUrl.searchParams.set('q', trimmed);
    suggestUrl.searchParams.set('resources[type]', 'product');
    suggestUrl.searchParams.set('resources[limit]', '5');
    suggestUrl.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(suggestUrl.toString(), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      if (requestId !== searchRequestId) return;
      renderSearchResults(data?.resources?.results?.products || [], trimmed);
    } catch (error) {
      if (requestId !== searchRequestId) return;
      console.error(error);
      renderSearchResults([], trimmed);
    }
  };

  if (searchTrigger) {
    searchTrigger.addEventListener('click', () => {
      setSearchPanelOpen(true);
      window.requestAnimationFrame(() => {
        const input = getActiveSearchInput();
        if (!input || !isSearchPanelOpen()) return;
        input.removeAttribute('readonly');
        input.focus({ preventScroll: true });
      });
    });
  }

  if (searchClose) {
    searchClose.addEventListener('click', () => setSearchPanelOpen(false));
  }

  if (searchOverlay) {
    searchOverlay.addEventListener('click', () => {
      setSearchPanelOpen(false);
    });
  }

  searchInputs.forEach((input) => {
    input.addEventListener('focus', () => {
      if (!isSearchPanelOpen()) {
        input.blur();
        return;
      }
      input.removeAttribute('readonly');
    });

    input.addEventListener('input', () => {
      window.clearTimeout(searchDebounceTimer);
      searchDebounceTimer = window.setTimeout(() => {
        fetchPredictiveSearch(input.value);
      }, 220);
    });
  });

  // Prevent ghost keyboard from a hidden search input (esp. iOS Safari / PDP sticky)
  syncSearchFocusability(false);
  setSearchPanelOpen(false);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (header?.classList.contains('header--search-panel-open')) {
      setSearchPanelOpen(false);
      return;
    }
    if (header?.classList.contains('header--search-open')) {
      setSearchResultsOpen(false);
      getActiveSearchInput()?.blur();
    }
  });

  // Mobile Navigation Drawer Handling
  const menuTrigger = document.querySelector('[data-mobile-menu-trigger]');
  const drawerClose = document.querySelector('[data-mobile-drawer-close]');
  const drawer = document.querySelector('[data-mobile-drawer]');
  const overlay = document.querySelector('[data-mobile-drawer-overlay]');

  if (drawer && drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }
  if (overlay && overlay.parentElement !== document.body) {
    document.body.appendChild(overlay);
  }

  let mobileMenuScrollY = 0;

  const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;

  const syncHeaderBottom = () => {
    if (!header) return;
    const rect = header.getBoundingClientRect();
    const bottom = Math.max(0, Math.ceil(rect.bottom));
    document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
  };

  const lockMobileMenuScroll = () => {
    mobileMenuScrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarWidth = getScrollbarWidth();

    document.documentElement.classList.add('is-mobile-menu-open');
    document.documentElement.style.setProperty('--mobile-menu-scrollbar-width', `${scrollbarWidth}px`);
    header?.classList.add('header--menu-open');
    menuTrigger?.setAttribute('aria-expanded', 'true');

    if (header?.classList.contains('header--scrolled')) {
      header.classList.add('header--drawer-preserve-scrolled');
    }

    syncHeaderBottom();
  };

  const unlockMobileMenuScroll = () => {
    document.documentElement.classList.remove('is-mobile-menu-open');
    document.documentElement.style.removeProperty('--mobile-menu-scrollbar-width');
    header?.classList.remove('header--drawer-preserve-scrolled');
    header?.classList.remove('header--menu-open');
    menuTrigger?.setAttribute('aria-expanded', 'false');
    window.scrollTo({ top: mobileMenuScrollY, left: 0, behavior: 'instant' });
    window.dispatchEvent(new Event('scroll'));
  };

  const openMobileMenu = () => {
    lockMobileMenuScroll();
    syncHeaderBottom();
    drawer?.classList.add('header__mobile-drawer--open');
    overlay?.classList.add('header__mobile-drawer-overlay--open');
    window.requestAnimationFrame(syncHeaderBottom);
  };

  const closeMobileMenu = () => {
    drawer?.classList.remove('header__mobile-drawer--open');
    overlay?.classList.remove('header__mobile-drawer-overlay--open');
    unlockMobileMenuScroll();
  };

  if (menuTrigger && drawer && overlay) {
    menuTrigger.setAttribute('aria-expanded', 'false');
    menuTrigger.addEventListener('click', () => {
      if (drawer.classList.contains('header__mobile-drawer--open')) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });
  }

  window.addEventListener('resize', syncHeaderBottom, { passive: true });
  window.addEventListener('scroll', syncHeaderBottom, { passive: true });
  syncHeaderBottom();

  if (drawerClose) drawerClose.addEventListener('click', closeMobileMenu);
  if (overlay) overlay.addEventListener('click', closeMobileMenu);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer?.classList.contains('header__mobile-drawer--open')) {
      closeMobileMenu();
    }
  });

  // Sticky & Transparent Header scrolled state
  if (header) {
    const isSticky = header.getAttribute('data-sticky') === 'true';
    const isTransparent = header.getAttribute('data-transparent') === 'true';
    const announcementBar = document.querySelector('[data-announcement-bar]');

    const syncAnnouncementOffset = () => {
      if (!announcementBar) {
        document.documentElement.style.setProperty('--announcement-bar-offset', '0px');
        return;
      }

      const rect = announcementBar.getBoundingClientRect();
      const offset = Math.max(0, Math.ceil(rect.bottom));
      document.documentElement.style.setProperty('--announcement-bar-offset', `${offset}px`);
    };

    syncAnnouncementOffset();
    window.addEventListener('resize', syncAnnouncementOffset, { passive: true });

    if (isSticky) {
      const section = header.closest('.section-header') || header.closest('.shopify-section');
      if (section) {
        section.classList.add('section-header--sticky');
      }
    }

    if (isSticky || isTransparent) {
      const onScroll = () => {
        syncAnnouncementOffset();

        if (document.body.classList.contains('is-cart-drawer-open')) {
          if (header.classList.contains('header--drawer-preserve-scrolled')) {
            header.classList.add('header--scrolled');
          }
          return;
        }

        if (document.documentElement.classList.contains('is-mobile-menu-open')) {
          if (header.classList.contains('header--drawer-preserve-scrolled')) {
            header.classList.add('header--scrolled');
          }
          return;
        }

        if (window.scrollY > 20) {
          header.classList.add('header--scrolled');
        } else {
          header.classList.remove('header--scrolled');
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll(); // initial check
    }
  }

  document.querySelectorAll('[data-language-switcher]').forEach((switcher) => {
    const trigger = switcher.querySelector('[data-language-switcher-trigger]');
    const panel = switcher.querySelector('[data-language-switcher-panel]');
    if (!trigger || !panel) return;

    const close = () => {
      switcher.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    };

    const open = () => {
      document.querySelectorAll('[data-language-switcher].is-open').forEach((other) => {
        if (other === switcher) return;
        const otherTrigger = other.querySelector('[data-language-switcher-trigger]');
        const otherPanel = other.querySelector('[data-language-switcher-panel]');
        other.classList.remove('is-open');
        otherTrigger?.setAttribute('aria-expanded', 'false');
        if (otherPanel) otherPanel.hidden = true;
      });

      switcher.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    };

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (switcher.classList.contains('is-open')) close();
      else open();
    });

    document.addEventListener('click', (event) => {
      if (!switcher.contains(event.target)) close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    switcher.querySelectorAll('[data-language-switcher-option]').forEach((option) => {
      option.addEventListener('click', (event) => {
        if (switcher.tagName !== 'FORM') return;

        event.preventDefault();

        const locale = option.getAttribute('data-locale');
        if (!locale) return;

        let localeInput = switcher.querySelector('input[name="locale_code"]');
        if (!localeInput) {
          localeInput = document.createElement('input');
          localeInput.type = 'hidden';
          localeInput.name = 'locale_code';
          switcher.appendChild(localeInput);
        }

        localeInput.value = locale;
        switcher.submit();
      });
    });
  });
});
